import { assignInlineVars } from "@vanilla-extract/dynamic";
import React from "react";
import {
    ComparePricesButton,
    ShippingInfo,
    ViewSellerDetails,
} from "web/react/components/buy-option-card";
import { Promotion } from "web/react/components/promotion/promotion";
import { HStack } from "web/react/emo/h-stack";
import { Price } from "web/react/emo/price";
import { RadioCardGroup } from "web/react/emo/radio-card-group";
import { RadioButtonWithLabelProps } from "web/react/emo/radio-card-group/radio-button-with-label";
import { RetailerWithLogo } from "web/react/emo/retailer-with-logo";
import { Sticker, StickerType } from "web/react/emo/sticker";
import { VStack } from "web/react/emo/v-stack";
import { View } from "web/react/emo/view";
import {
    AffiliateBuyButton,
    CheckoutBuyButton,
} from "web/react/pages/product/in-stock/in-stock-product-page/buy-button/";
import {
    BuyOptionPromotionSerializer,
    ShippingInformation,
} from "web/react/pages/product/in-stock/in-stock-product-page/in-stock-product-page.context";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import { ProductBuyOptionsSerializer } from "web/types/serializers";
import * as styles from "./buy-option-card.css";

export interface BuyOptionCardBaseProps {
    buyOption: ProductBuyOptionsSerializer;
    parentArea: "product_details" | "price_comparison";
    retailerLogoURL?: string;
    stickers: StickerType[];
    shippingInformation: ShippingInformation[];
    promotion: BuyOptionPromotionSerializer | null;
    isCheckout?: boolean;
}

type CheckoutProps =
    | {
          isCheckout: true;
          buyOptionIndex: number;
      }
    | {
          isCheckout?: false;
          buyOptionIndex?: never;
      };

type NarrowCardProps = {
    variant: "narrow";
    comparePricesCount: number;
    onBuyButtonClick?: () => void;
    onClickCompare?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
    onShowCompare?: () => void;
    onRetailerClick?: () => void;
} & CheckoutProps &
    BuyOptionCardBaseProps;

type WideCardProps = {
    variant: "wide";
    onBuyButtonClick?: () => void;
    onRetailerClick?: () => void;
} & CheckoutProps &
    BuyOptionCardBaseProps;

interface RadioCardProps extends BuyOptionCardBaseProps, RadioButtonWithLabelProps {
    variant: "radio";
}

export type BuyOptionCardProps = NarrowCardProps | WideCardProps | RadioCardProps;

export function BuyOptionCard(props: BuyOptionCardProps): React.ReactElement | null {
    const {
        variant,
        buyOption,
        parentArea,
        retailerLogoURL,
        stickers,
        shippingInformation,
        promotion = null,
        isCheckout = false,
    } = props;

    const isDeepAffiliatePhase1Enabled = environment.getFeature(
        "deep_affiliate_experiment_phase_1"
    );
    const isStorefrontsEnabled = environment.getFeature("beam_checkout_storefronts");

    const isCheckoutBuyOption = !!buyOption.is_icon && isCheckout;
    const isVerifiedRetailer = isCheckoutBuyOption && !isDeepAffiliatePhase1Enabled;

    const shouldShowViewSellerDetails = isStorefrontsEnabled && !isDeepAffiliatePhase1Enabled;

    if (variant === "radio") {
        const { id, name, value, checked, onChange } = props;

        return (
            <RadioCardGroup.Card
                banner={
                    promotion ? (
                        <Promotion
                            pageType="pdp"
                            message={promotion.message}
                            promoCode={promotion.promoCode}
                            isActive={true}
                            promoAnalyticsItemData={{
                                clicked: { parent_area: parentArea },
                                impression: { parent_area: parentArea },
                            }}
                        />
                    ) : null
                }
                selected={checked}
                onClick={onChange}
            >
                <div className={styles.radioCard} data-testid="buy-option-card-radio">
                    <div className={styles.row}>
                        <RadioCardGroup.Button
                            id={id}
                            name={name}
                            value={value}
                            checked={checked}
                            onChange={onChange}
                        >
                            <Price
                                fullPrice={buyOption.price}
                                inStock={Boolean(buyOption.in_stock)}
                                salePrice={buyOption.sale_price}
                            />
                        </RadioCardGroup.Button>
                        <div className={styles.retailerWithLogo}>
                            <RetailerWithLogo
                                retailerName={buyOption.retailer_name}
                                retailerLogoURL={retailerLogoURL}
                                isVerified={isVerifiedRetailer}
                            />
                        </div>
                    </div>
                    {!!stickers && (
                        <div className={styles.stickerContainer}>
                            {stickers.map((stickerType: StickerType) => (
                                <Sticker key={stickerType} name={stickerType} />
                            ))}
                        </div>
                    )}
                    <ShippingInfo
                        buyOptionId={buyOption.link_id}
                        shippingInformation={shippingInformation}
                        direction={"column"}
                    />
                </div>
            </RadioCardGroup.Card>
        );
    }

    const { onBuyButtonClick, onRetailerClick } = props;

    const titles = {
        checkout: gettext("checkout.buy_now"),
        default: gettext("product.buy_from_store"),
    };
    const longestTextLength = Math.max(...Object.values(titles).map((key) => key.length));

    const baseButtonProps = {
        ...(onBuyButtonClick ? { onClick: onBuyButtonClick } : {}),
        ...(variant === "wide" ? { width: "fit-content" as const } : { width: "full" as const }),
    };

    const handleClick = (parentArea: string): void => {
        analytics.event(
            "pdp_seller_details",
            "clicked",
            "",
            false,
            {},
            "",
            {},
            [{ retailer_network: buyOption.retailer_name }, { parent_area: parentArea }],
            false,
            false
        );
    };

    if (variant === "narrow") {
        const { comparePricesCount, onClickCompare, onShowCompare } = props;
        return (
            <div className={styles.narrowCard} data-testid="buy-option-card-narrow">
                <div className={styles.row}>
                    <Price
                        fullPrice={buyOption.price}
                        inStock={Boolean(buyOption.in_stock)}
                        salePrice={buyOption.sale_price}
                    />

                    <a
                        href={buyOption.retailer_link}
                        data-testid="retailer-internal-link"
                        onClick={onRetailerClick}
                    >
                        <div className={styles.retailerWithLogo}>
                            <RetailerWithLogo
                                retailerName={buyOption.retailer_name}
                                retailerLogoURL={retailerLogoURL}
                                isVerified={isVerifiedRetailer}
                            />
                        </div>
                    </a>
                </div>
                {!!stickers && (
                    <div className={styles.stickerContainer}>
                        {stickers.map((stickerType: StickerType) => (
                            <Sticker key={stickerType} name={stickerType} />
                        ))}
                    </div>
                )}
                <ShippingInfo
                    buyOptionId={buyOption.link_id}
                    shippingInformation={shippingInformation}
                    direction={"column"}
                />
                {promotion && (
                    <div className={styles.narrowCardPromo}>
                        <Promotion
                            pageType="pdp"
                            message={promotion.message}
                            promoCode={promotion.promoCode}
                            isActive={true}
                        />
                    </div>
                )}
                <VStack spacing="sm">
                    <HStack spacing="xxs">
                        <div className={styles.fullWidth}>
                            {props.isCheckout && buyOption.is_icon ? (
                                <CheckoutBuyButton
                                    buyOption={buyOption}
                                    buyOptionPosition={props.buyOptionIndex}
                                    sendExtraAnalytics={false}
                                    buttonProps={{
                                        ...baseButtonProps,
                                        title: titles.checkout,
                                    }}
                                    parentArea={parentArea}
                                />
                            ) : (
                                <AffiliateBuyButton
                                    buyOption={buyOption}
                                    isComparison={true}
                                    buttonProps={{
                                        ...baseButtonProps,
                                        title: titles.default,
                                        icon: "external-link",
                                        iconPosition: "right",
                                    }}
                                />
                            )}
                        </div>
                        {comparePricesCount > 1 && (
                            <ComparePricesButton
                                onClick={onClickCompare}
                                onShow={onShowCompare}
                                count={comparePricesCount}
                            />
                        )}
                    </HStack>
                    {shouldShowViewSellerDetails && (
                        <View paddingBottom="xxs" width="full">
                            <ViewSellerDetails
                                prefetch
                                buyOption={buyOption}
                                onClick={() => handleClick("product_details")}
                            />
                        </View>
                    )}
                </VStack>
            </div>
        );
    }

    return (
        <div className={styles.wideCard} data-testid="buy-option-card-wide">
            <div className={styles.wideCardInner}>
                <div className={styles.wideInfoArea}>
                    <div className={styles.widePriceAndRetailerArea}>
                        <Price
                            fullPrice={buyOption.price}
                            inStock={Boolean(buyOption.in_stock)}
                            salePrice={buyOption.sale_price}
                        />
                        <a
                            href={buyOption.retailer_link}
                            data-testid="retailer-internal-link"
                            onClick={onRetailerClick}
                        >
                            <RetailerWithLogo
                                retailerName={buyOption.retailer_name}
                                retailerLogoURL={retailerLogoURL}
                                isVerified={isVerifiedRetailer}
                            />
                        </a>
                    </div>
                    {!!stickers && (
                        <div className={styles.stickerContainer}>
                            {stickers.map((stickerType: StickerType) => (
                                <Sticker key={stickerType} name={stickerType} />
                            ))}
                        </div>
                    )}
                    <ShippingInfo
                        buyOptionId={buyOption.link_id}
                        shippingInformation={shippingInformation}
                        direction={"row"}
                    />
                </div>
                <div className={styles.wideCtaArea}>
                    <div
                        className={styles.wideCtaWrapper}
                        style={assignInlineVars({
                            [styles.maxWordsInBuyButton]: `${longestTextLength}`,
                        })}
                    >
                        <View width="full">
                            {props.isCheckout && buyOption.is_icon ? (
                                <CheckoutBuyButton
                                    buyOption={buyOption}
                                    buyOptionPosition={props.buyOptionIndex}
                                    sendExtraAnalytics={false}
                                    buttonProps={{
                                        ...baseButtonProps,
                                        title: titles.checkout,
                                        width: "full",
                                    }}
                                    parentArea={parentArea}
                                />
                            ) : (
                                <AffiliateBuyButton
                                    buyOption={buyOption}
                                    isComparison={true}
                                    buttonProps={{
                                        ...baseButtonProps,
                                        title: titles.default,
                                        icon: "external-link",
                                        iconPosition: "right",
                                        width: "full",
                                    }}
                                />
                            )}
                        </View>
                        {shouldShowViewSellerDetails && (
                            <ViewSellerDetails
                                buyOption={buyOption}
                                onClick={() => handleClick("price_comparison")}
                            />
                        )}
                    </div>
                </div>
            </div>
            {promotion && (
                <div className={styles.wideCardPromo}>
                    <Promotion
                        pageType="pdp"
                        message={promotion.message}
                        promoCode={promotion.promoCode}
                        isActive={true}
                    />
                </div>
            )}
        </div>
    );
}
